import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ProfileType } from 'shared';
import { haptic } from './telegram';
import { useBackButton } from './useBackButton';
import { markTourSeen, stepIndex, tourFor } from './tourState';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Сколько ждём появления якоря после перехода в раздел. Экран успевает
 * смонтироваться за пару кадров, но первый заход грузит данные по сети,
 * и кнопка может появиться позже самой разметки.
 */
const WAIT_MS = 1500;

/** Зазор между подсветкой и подсказкой. */
const GAP = 12;
/** Поля от краёв экрана. */
const EDGE = 12;

/**
 * Видимый элемент с этим якорем.
 *
 * Именно видимый: боковое меню и нижние вкладки держат одни и те же
 * разделы и живут в разметке одновременно — лишнее прячет ширина экрана.
 * Простой `querySelector` вернул бы скрытую копию, а у неё нулевые
 * размеры, и подсветка встала бы в левый верхний угол.
 */
function anchorEl(anchor: string): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(`[data-tour="${anchor}"]`);
  for (const el of all) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

function boxOf(el: HTMLElement): Box {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function reducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

interface Props {
  profile: ProfileType;
  /** Открыть раздел, которого требует шаг. */
  onGoScreen: (name: string) => void;
  onClose: () => void;
}

/**
 * Обучающий тур по площадке: затемнение с окном вокруг элемента и
 * подсказка рядом.
 *
 * Своя реализация, а не библиотека: в приложении три рантайм-зависимости,
 * и заводить четвёртую ради подсветки прямоугольника незачем — тему,
 * тактильный отклик, стек кнопки «назад» и хранение отметки всё равно
 * пришлось бы писать руками поверх чужого кода.
 *
 * Шаг, чей элемент не нашёлся, пропускается: разделы скрываются флагами
 * функций и правами, и упираться в кнопку, которой у человека нет, — худшее,
 * что может сделать обучение.
 */
export default function Tour({ profile, onGoScreen, onClose }: Props) {
  const steps = useMemo(() => tourFor(profile), [profile]);
  const [i, setI] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  /** Пока ищем якорь, подсказку не показываем — иначе она мигает в углу. */
  const [ready, setReady] = useState(false);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const pop = useRef<HTMLDivElement>(null);

  const step = steps[i];

  const finish = useCallback(() => {
    markTourSeen();
    onClose();
  }, [onClose]);

  /** Переход по туру с пропуском шагов, которым некуда крепиться. */
  const go = useCallback(
    (dir: 1 | -1) => {
      const next = stepIndex(steps, i + dir, dir, (a) => anchorEl(a) !== null);
      if (next === null) {
        if (dir === 1) finish();
        return;
      }
      haptic();
      setReady(false);
      setBox(null);
      setPlace(null);
      setI(next);
    },
    [finish, i, steps],
  );

  useBackButton(true, finish);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [finish]);

  /* Открыть нужный раздел, дождаться якоря, подвести его к центру. */
  useEffect(() => {
    if (!step) {
      finish();
      return;
    }
    if (step.screen) onGoScreen(step.screen);

    let alive = true;
    const deadline = Date.now() + WAIT_MS;

    const look = () => {
      if (!alive) return;
      const el = anchorEl(step.anchor);
      if (el) {
        el.scrollIntoView({
          block: 'center',
          inline: 'nearest',
          behavior: reducedMotion() ? 'auto' : 'smooth',
        });
        setBox(boxOf(el));
        setReady(true);
        return;
      }
      if (Date.now() > deadline) {
        /*
         * Якоря нет и не будет: раздел выключен флагом или недоступен по
         * правам. Уходим дальше, а на последнем шаге просто заканчиваем.
         */
        const next = stepIndex(steps, i + 1, 1, (a) => anchorEl(a) !== null);
        if (next === null) finish();
        else setI(next);
        return;
      }
      requestAnimationFrame(look);
    };

    requestAnimationFrame(look);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, step?.anchor, step?.screen]);

  /* Подсветка обязана ехать за элементом: экран докручивается плавно,
     а внутренние списки прокручиваются отдельно от окна. */
  useEffect(() => {
    if (!ready || !step) return;
    const sync = () => {
      const el = anchorEl(step.anchor);
      if (el) setBox(boxOf(el));
    };
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    const timer = window.setInterval(sync, 250);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
      window.clearInterval(timer);
    };
  }, [ready, step]);

  /* Положение подсказки считаем после того, как узнали её высоту. */
  useLayoutEffect(() => {
    if (!ready || !box) return;
    const el = pop.current;
    if (!el) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = el.offsetWidth;
    const h = el.offsetHeight;

    const below = box.top + box.height + GAP;
    const above = box.top - GAP - h;
    const top = below + h + EDGE <= vh ? below : Math.max(EDGE, above);

    const centered = box.left + box.width / 2 - w / 2;
    const left = Math.min(Math.max(EDGE, centered), Math.max(EDGE, vw - w - EDGE));

    setPlace({ top, left });
  }, [ready, box]);

  if (!step) return null;

  const total = steps.length;

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label="Обучение">
      {/*
        Перехватчик касаний — отдельно от затемнения. Затемнение рисуется
        тенью вокруг окна, а тень не участвует в попадании: без этого слоя
        сквозь «затемнённую» часть продолжают нажиматься кнопки приложения.
      */}
      <div className={box ? 'tour__block' : 'tour__block tour__block--dim'} />
      {box && (
        <div
          className="tour__hole"
          style={{
            top: box.top - 6,
            left: box.left - 6,
            width: box.width + 12,
            height: box.height + 12,
          }}
        />
      )}

      <div
        ref={pop}
        className="tour__pop"
        style={place ? { top: place.top, left: place.left } : { visibility: 'hidden' }}
      >
        <div className="tour__count">
          Шаг {i + 1} из {total}
        </div>
        <h3 className="tour__title">{step.title}</h3>
        <p className="tour__text">{step.text}</p>
        <div className="tour__actions">
          <button type="button" className="ghost btn--compact" onClick={finish}>
            Пропустить
          </button>
          <div className="tour__nav">
            {i > 0 && (
              <button type="button" className="secondary btn--compact" onClick={() => go(-1)}>
                Назад
              </button>
            )}
            <button type="button" className="btn--compact" onClick={() => go(1)}>
              {i + 1 >= total ? 'Понятно' : 'Далее'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
